// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
// @greenlight-spec: decisions/0039-injected-grant-is-disclosure.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';

import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { ListCard } from '@/components/greenlight/ListCard';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Button } from '@/components/ui/button';
import { CredentialChip } from '@/components/permissions/CredentialChip';
import { HeldReason } from '@/components/permissions/HeldReason';
import type { PermissionListItem } from '@greenlight/shared';

// ---------------------------------------------------------------------------
// Status → badge metadata map (parallel to PERM_META in app-detail/index.tsx,
// but local to this component so it can be maintained independently).
// ---------------------------------------------------------------------------

const PERM_META: Record<string, { variant: StatusBadgeVariant; label: string }> = {
  granted: { variant: 'granted', label: 'Granted' },
  pending: { variant: 'pending', label: 'Pending' },
  denied: { variant: 'failed', label: 'Denied' },
  revoked: { variant: 'revoked', label: 'Revoked' },
};

/** Which entity titles the row. The card identity is the *other* entity, so the
 * row leads with whichever one varies within the card (docs/28 § /permissions). */
export type PermissionRowPrimary = 'integration' | 'accessHolder';

/** Label-above-value cell for the mobile card's field grid. */
function CardField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-[13px] text-foreground">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PermissionRowProps {
  item: PermissionListItem;
  /**
   * Entity that titles the row. `integration` (default) leads with the
   * integration tile (grouped by access holder); `accessHolder` leads with the
   * app/person tile (grouped by integration). The titled entity links to its
   * home surface.
   */
  primary?: PermissionRowPrimary;
  /** True while a decision mutation for THIS row is in flight (disables buttons). */
  pending?: boolean;
  /** Render the compact card layout instead of the desktop row. The view picks
   * the breakpoint (the Grants sub-view switches at lg). Default false. */
  isMobile?: boolean;
  onApprove: (item: PermissionListItem) => void;
  onDeny: (item: PermissionListItem) => void;
  onRevoke: (item: PermissionListItem) => void;
}

// ---------------------------------------------------------------------------
// Identity helpers — the titled entity, its link, and its tile
// ---------------------------------------------------------------------------

// The access-holder-titled row: an app grant leads with the app, a user grant with
// the person (explicit principal_type, never inferred from names — ADR-0029).
function titleName(item: PermissionListItem, primary: PermissionRowPrimary): string {
  if (primary !== 'accessHolder') return item.integration_name;
  return item.principal_type === 'app' ? (item.app_name ?? 'Unknown app') : item.requested_by;
}

/** A person access holder has no app page; its title renders unlinked (null href). */
function titleHref(item: PermissionListItem, primary: PermissionRowPrimary): string | null {
  if (primary !== 'accessHolder') return `/integrations?integration=${item.integration_slug}`;
  return item.app_id !== null ? `/apps/${item.app_id}?tab=permissions` : null;
}

function TitleTile({
  item,
  primary,
  size,
}: {
  item: PermissionListItem;
  primary: PermissionRowPrimary;
  size: number;
}): React.JSX.Element {
  if (primary !== 'accessHolder') {
    return (
      <IntegrationTile
        slug={item.integration_slug}
        displayName={item.integration_name}
        size={size}
        className="shrink-0"
      />
    );
  }
  if (item.principal_type === 'user') {
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-gl bg-muted text-muted-foreground"
        style={{ width: size, height: size }}
        role="img"
        aria-label="Person access holder"
      >
        <User className="h-4 w-4" />
      </span>
    );
  }
  return (
    <AppIconTile
      name={item.app_name ?? 'Unknown app'}
      slug={item.app_slug ?? ''}
      size={size}
      className="shrink-0"
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PermissionRow({
  item,
  primary = 'integration',
  pending = false,
  isMobile = false,
  onApprove,
  onDeny,
  onRevoke,
}: PermissionRowProps): React.JSX.Element {
  const statusMeta = PERM_META[item.status] ?? {
    variant: 'inactive' as StatusBadgeVariant,
    label: item.status,
  };
  const showStatusBadge = item.status !== 'pending';
  const isUserHolder = item.principal_type === 'user';
  const showHolderType = primary === 'accessHolder';
  const name = titleName(item, primary);
  const href = titleHref(item, primary);

  if (isMobile) {
    const showActions = item.status === 'pending' || item.status === 'granted';
    return (
      <ListCard data-testid={`permission-row-${item.id}`}>
        {/* Header: identity (tile over scope) + body (left) · status badge (upper-right) */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {/* Left column: tile on top, scope slug centered beneath (scope leads here too) */}
            <div className="flex shrink-0 flex-col items-center gap-1">
              <TitleTile item={item} primary={primary} size={38} />
              <CredentialChip slug={item.credential_slug} />
            </div>
            <div className="min-w-0">
              {href !== null ? (
                <Link
                  to={href}
                  className="text-[15px] font-semibold text-foreground underline-offset-2 hover:underline"
                >
                  {name}
                </Link>
              ) : (
                <span className="text-[15px] font-semibold text-foreground">{name}</span>
              )}
              {showHolderType && (
                <span className="ml-2 align-middle font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
                  {isUserHolder ? 'Person' : 'App'}
                </span>
              )}
              {item.credential_scope && (
                <p className="mt-1 text-[12px] leading-snug text-muted-foreground">
                  {item.credential_scope}
                </p>
              )}
              {item.reason && (
                <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
                  &ldquo;{item.reason}&rdquo;
                </p>
              )}
              {item.held_reason && <HeldReason reason={item.held_reason} className="mt-1" />}
            </div>
          </div>
          {showStatusBadge && (
            <div className="shrink-0">
              <StatusBadge variant={statusMeta.variant}>{statusMeta.label}</StatusBadge>
            </div>
          )}
        </div>

        {/* Facts (left) + actions aligned to the bottom-right. */}
        <div className="mt-3 flex items-end justify-between gap-4 border-t border-border pt-3">
          <dl className="min-w-0 space-y-2">
            <CardField label="Requested">
              <span className="inline-flex items-center gap-1">
                {isUserHolder && (
                  <User className="inline h-3 w-3" aria-label="Person access holder" role="img" />
                )}
                {item.requested_by}
              </span>
              {' · '}
              <span title={item.requested_at}>{relativeTime(item.requested_at)}</span>
            </CardField>
            {item.auto_approved ? (
              <CardField label="Approved">Auto-approved by policy</CardField>
            ) : (
              (item.reviewed_by || item.reviewed_at) && (
                <CardField label="Reviewed">
                  {item.reviewed_by}
                  {item.reviewed_by && item.reviewed_at ? ' · ' : ''}
                  {item.reviewed_at && (
                    <span title={item.reviewed_at}>{relativeTime(item.reviewed_at)}</span>
                  )}
                </CardField>
              )
            )}
          </dl>

          {showActions && (
            <div className="flex shrink-0 items-center gap-2">
              {item.status === 'pending' && (
                <>
                  <Button
                    size="sm"
                    variant="destructive-outline"
                    className="h-11"
                    disabled={pending}
                    onClick={() => onDeny(item)}
                  >
                    Deny
                  </Button>
                  <Button
                    size="sm"
                    className="h-11"
                    disabled={pending || item.held_reason != null}
                    title={
                      item.held_reason != null
                        ? 'Held until the app’s access is narrowed'
                        : undefined
                    }
                    onClick={() => onApprove(item)}
                  >
                    Approve
                  </Button>
                </>
              )}
              {item.status === 'granted' && (
                <Button
                  size="sm"
                  variant="destructive-outline"
                  className="h-11"
                  disabled={pending}
                  onClick={() => onRevoke(item)}
                >
                  Revoke
                </Button>
              )}
            </div>
          )}
        </div>
      </ListCard>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`permission-row-${item.id}`}>
      {/* Zone 1 — credential scope leads the row (the handle an IT reviewer scans for first) */}
      <div className="flex w-36 shrink-0 items-center">
        <CredentialChip slug={item.credential_slug} />
      </div>

      {/* Zone 2 — identity + body: tile, linked name, description, reason */}
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <TitleTile item={item} primary={primary} size={38} />
        <div className="min-w-0 flex-1">
          {href !== null ? (
            <Link
              to={href}
              className="text-[13.5px] font-semibold text-foreground underline-offset-2 hover:underline"
            >
              {name}
            </Link>
          ) : (
            <span className="text-[13.5px] font-semibold text-foreground">{name}</span>
          )}
          {showHolderType && (
            <span className="ml-2 align-middle font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
              {isUserHolder ? 'Person' : 'App'}
            </span>
          )}
          {item.credential_scope && (
            <p className="mt-0.5 text-[11.5px] text-muted-foreground">{item.credential_scope}</p>
          )}
          {item.reason && (
            <p className="mt-0.5 text-[12px] text-muted-foreground">&ldquo;{item.reason}&rdquo;</p>
          )}
          {item.held_reason && <HeldReason reason={item.held_reason} className="mt-0.5" />}
          {item.auto_approved && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">Auto-approved by policy</p>
          )}
        </div>
      </div>

      {/* Zone 3 — requestor/date as its own aligned column (actor over the relative date) */}
      <div className="hidden w-32 shrink-0 flex-col items-end text-right lg:flex">
        <span className="flex max-w-full items-center gap-1 text-[12px] text-muted-foreground">
          {isUserHolder && (
            <User
              className="inline h-3 w-3 shrink-0"
              aria-label="Person access holder"
              role="img"
            />
          )}
          <span className="min-w-0 truncate">{item.requested_by}</span>
        </span>
        <span className="mt-0.5 text-[11px] text-muted-foreground" title={item.requested_at}>
          {relativeTime(item.requested_at)}
        </span>
      </div>

      {/* Zone 4 — status + actions, fixed-width right slot so zone 3 stays aligned */}
      <div className="flex w-44 shrink-0 items-center justify-end gap-3">
        {showStatusBadge && (
          <StatusBadge variant={statusMeta.variant}>{statusMeta.label}</StatusBadge>
        )}
        {(item.status === 'pending' || item.status === 'granted') && (
          <div className="flex items-center gap-2">
            {item.status === 'pending' && (
              <>
                <Button
                  size="sm"
                  variant="destructive-outline"
                  disabled={pending}
                  onClick={() => onDeny(item)}
                  className="h-11 md:h-8"
                >
                  Deny
                </Button>
                <Button
                  size="sm"
                  disabled={pending || item.held_reason != null}
                  title={
                    item.held_reason != null ? 'Held until the app’s access is narrowed' : undefined
                  }
                  onClick={() => onApprove(item)}
                  className="h-11 md:h-8"
                >
                  Approve
                </Button>
              </>
            )}
            {item.status === 'granted' && (
              <Button
                size="sm"
                variant="destructive-outline"
                disabled={pending}
                onClick={() => onRevoke(item)}
                className="h-11 md:h-8"
              >
                Revoke
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
